import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListCustomObjectsResult } from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  Field,
  Icon,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Setup > Objects > New object (F-010).
 *
 * Three steps, because the questions are of different kinds and mixing them is
 * how somebody ends up choosing an API name they cannot change while thinking
 * about an icon. Name, then API name, then who can see it - and the last one
 * is asked at creation rather than defaulted, because an object nobody can see
 * is not an object that exists.
 */

const SHARING = ['private', 'team', 'everyone'] as const;

/** Suggests an API name from the plural, so most people never type one. */
export function suggestKey(plural: string): string {
  return plural
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/s$/, '')
    .slice(0, 40);
}

export function CustomObjectsPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [label, setLabel] = useState('');
  const [plural, setPlural] = useState('');
  const [objectKey, setObjectKey] = useState('');
  const [sharing, setSharing] = useState<(typeof SHARING)[number]>('private');

  /*
   * F-010 ships off, so the panel has to say so rather than fail.
   *
   * A locked feature that renders an error tells an administrator their
   * software is broken, when what is true is that a feature is off and they
   * are the person who can turn it on.
   */
  const entitlement = useEntitlement('F-010');
  const objects = useQuery<ListCustomObjectsResult, ApiError>({
    queryKey: ['custom-objects'],
    queryFn: () => api.listCustomObjects({}),
    // Not asked at all when it is off: a request certain to be refused is a
    // refusal in the network tab and a spinner on the screen.
    enabled: entitlement.state === 'on',
  });

  const create = useMutation({
    mutationFn: () =>
      api.createCustomObject({
        body: { objectKey, label, plural, icon: 'Package', sharing },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-objects'] });
      setStep(0);
      setLabel('');
      setPlural('');
      setObjectKey('');
    },
  });

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="custom-objects" id="objects">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('customObject.title')}</SectionLabel>
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('customObject.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('customObject.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (objects.isPending) return <Skeleton className="h-block-md w-full" />;
  if (objects.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={objects.error.message}
        onRetry={() => void objects.refetch()}
      />
    );
  }

  return (
    <Panel data-region="custom-objects" id="objects">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>{t('customObject.title')}</SectionLabel>
          <Button
            intent="secondary"
            size="small"
            icon="Plus"
            data-new-object
            aria-expanded={step > 0}
            onClick={() => setStep((current) => (current === 0 ? 1 : 0))}
          >
            {t('customObject.new')}
          </Button>
        </div>
        <p className="text-meta text-ink-meta">{t('customObject.body')}</p>

        {objects.data.objects.length === 0 ? (
          <p className="text-dense text-ink-meta">{t('customObject.empty')}</p>
        ) : (
          <ul className="flex flex-col">
            {objects.data.objects.map((object) => (
              <li
                key={object.objectKey}
                data-custom-object={object.objectKey}
                className="flex items-baseline gap-3 border-t border-t-rule py-2 first:border-t-0"
              >
                <Icon name={object.icon} size="inline" decorative />
                <span className="text-body text-ink">{object.plural}</span>
                <span className="text-dense text-ink-meta">{object.objectKey}</span>
                <Num className="ms-auto text-dense text-ink-meta">
                  {object.records === 0
                    ? t('customObject.none')
                    : t('customObject.records', {
                        values: { count: formatCount(object.records, format) },
                      })}
                </Num>
              </li>
            ))}
          </ul>
        )}

        {step === 0 ? null : (
          <div data-object-wizard className="flex flex-col gap-3 rounded-panel border border-rule p-3">
            {/* One question at a time. Choosing an API name you cannot change
                while thinking about an icon is how people end up with one they
                regret. */}
            {step === 1 ? (
              <>
                <SectionLabel>{t('customObject.step1')}</SectionLabel>
                <Field
                  label={t('customObject.label')}
                  data-object-label
                  value={label}
                  onChange={(event) => setLabel(event.currentTarget.value)}
                />
                <Field
                  label={t('customObject.plural')}
                  data-object-plural
                  value={plural}
                  onChange={(event) => {
                    const next = event.currentTarget.value;
                    setPlural(next);
                    // Suggested, never forced: the person can still type their
                    // own, and most never have to.
                    setObjectKey(suggestKey(next));
                  }}
                />
                <div className="flex gap-2">
                  <Button
                    intent="primary"
                    size="small"
                    disabled={label.trim() === '' || plural.trim() === ''}
                    onClick={() => setStep(2)}
                  >
                    {t('permissions.save')}
                  </Button>
                  <Button intent="ghost" size="small" onClick={() => setStep(0)}>
                    {t('permissions.cancel')}
                  </Button>
                </div>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <SectionLabel>{t('customObject.step2')}</SectionLabel>
                <Field
                  label={t('customObject.key')}
                  data-object-key
                  value={objectKey}
                  hint={t('customObject.key.help')}
                  onChange={(event) => setObjectKey(event.currentTarget.value)}
                />
                <div className="flex gap-2">
                  <Button intent="primary" size="small" disabled={objectKey.trim() === ''} onClick={() => setStep(3)}>
                    {t('permissions.save')}
                  </Button>
                  <Button intent="ghost" size="small" onClick={() => setStep(1)}>
                    {t('permissions.cancel')}
                  </Button>
                </div>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <SectionLabel>{t('customObject.step3')}</SectionLabel>
                {SHARING.map((mode) => (
                  <label key={mode} className="flex gap-2">
                    <input
                      type="radio"
                      name="new-object-sharing"
                      data-object-sharing={mode}
                      checked={sharing === mode}
                      onChange={() => setSharing(mode)}
                      className="mt-1"
                    />
                    <span className="flex flex-col">
                      <span className="text-body text-ink">{t(`sharing.mode.${mode}` as never)}</span>
                      <span className="text-dense text-ink-meta">
                        {t(`sharing.mode.${mode}.help` as never)}
                      </span>
                    </span>
                  </label>
                ))}
                {create.isError ? (
                  <p data-object-refusal className="text-dense text-danger-text">
                    {create.error.message}
                  </p>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    intent="primary"
                    size="small"
                    data-create-object
                    busy={create.isPending}
                    onClick={() => create.mutate()}
                  >
                    {t('customObject.create')}
                  </Button>
                  <Button intent="ghost" size="small" onClick={() => setStep(2)}>
                    {t('permissions.cancel')}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </Panel>
  );
}
